import { useState, useCallback, useRef } from 'react'
import { sendStructuredSSE } from '../components/sseApi'
import API from '../helper/api'
import type { ChatEditChange, WebsiteBlock } from '../types'

interface EditMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  actionType?: string
  changes?: ChatEditChange[]
  updatedBlocks?: WebsiteBlock[]
}

export function useWebsiteEditor(websiteId: string) {
  const [messages, setMessages] = useState<EditMessage[]>([])
  const [isEditing, setIsEditing] = useState(false)
  const [streamingText, setStreamingText] = useState('')
  const [editHistory, setEditHistory] = useState<ChatEditChange[][]>([])
  const abortRef = useRef<AbortController | null>(null)

  const sendEditInstruction = useCallback((instruction: string, pageId?: string, blockId?: string) => {
    const userMsg: EditMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: instruction,
    }
    setMessages((prev) => [...prev, userMsg])
    setIsEditing(true)
    setStreamingText('')

    const controller = sendStructuredSSE(
      {
        websiteId,
        instruction,
        pageId: pageId || '',
        blockId: blockId || '',
      },
      API.GET_WEBSITE_CHAT_EDIT,
      // onThinking
      (text: string) => {
        setStreamingText((prev) => prev + text)
      },
      // onAction — UPDATE_BLOCK/ADD_BLOCK/REMOVE_BLOCK/TOGGLE_BLOCK
      (type: string, data: any) => {
        const assistantMsg: EditMessage = {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: data.message || getActionLabel(type),
          actionType: type,
          changes: data.changes,
          updatedBlocks: data.updatedBlocks,
        }
        setMessages((prevMsgs) => [...prevMsgs, assistantMsg])
        if (data.changes) {
          setEditHistory((prev) => [...prev, data.changes])
        }
        setStreamingText('')
        setIsEditing(false)
      },
      // onDone
      () => {
        setStreamingText((prev) => {
          if (prev) {
            setMessages((prevMsgs) => [
              ...prevMsgs,
              { id: `a-${Date.now()}`, role: 'assistant', content: prev },
            ])
          }
          return ''
        })
        setIsEditing(false)
      },
      // onError
      (err: Error) => {
        console.error('Chat edit SSE error:', err)
        setStreamingText('')
        setIsEditing(false)
      }
    )
    abortRef.current = controller
  }, [websiteId])

  const undoLastEdit = useCallback((): ChatEditChange[] | null => {
    if (editHistory.length === 0) return null
    const lastChanges = editHistory[editHistory.length - 1]
    setEditHistory((prev) => prev.slice(0, -1))
    return lastChanges
  }, [editHistory])

  const clearMessages = useCallback(() => {
    setMessages([])
    setStreamingText('')
  }, [])

  return {
    messages,
    isEditing,
    streamingText,
    editHistory,
    sendEditInstruction,
    undoLastEdit,
    clearMessages,
  }
}

function getActionLabel(type: string): string {
  switch (type) {
    case 'UPDATE_BLOCK': return '已修改区块'
    case 'ADD_BLOCK': return '已添加区块'
    case 'REMOVE_BLOCK': return '已删除区块'
    case 'TOGGLE_BLOCK': return '已切换区块可见性'
    default: return '操作完成'
  }
}
